import { useNavigate } from "@tanstack/react-router";
import {
  AlertOctagonIcon,
  BuildingIcon,
  CheckCircle2Icon,
  FilterIcon,
  FlagIcon,
  FlaskConicalIcon,
  PencilIcon,
  SendIcon,
  SlidersHorizontalIcon,
  TrashIcon,
  UserIcon,
  XCircleIcon,
  XIcon,
} from "lucide-react";
import React, { type Key, useEffect, useState } from "react";

import type { AccessionListFilter } from "@blis/shared/schemas/accession";
import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { RoleCondition } from "@/components/RoleCondition";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { FieldError, Input, Label, TextField } from "@/components/ui/field";
import { Form } from "@/components/ui/form";
import { StatusIcon } from "@/components/ui/icons";
import { ItemDetail } from "@/components/ui/list-box";
import { Modal } from "@/components/ui/modal";
import { Popover } from "@/components/ui/popover";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tab, Tabs, TabsList } from "@/components/ui/tabs";
import { Tag, TagGroup, TagList } from "@/components/ui/tag-group";
import { Text } from "@/components/ui/text";
import { useMediaQuery } from "@/components/ui/utils";
import { useAuth } from "@/features/auth/hooks";
import { useView } from "@/features/view/components/ViewProvider";
import type { TAccessionStatus, TTestType, TView } from "@/lib/types";
import { useTinyKeys } from "@/lib/useTinyKeys";
import { mutationResponse } from "@/utils/trpc";
import { deletePersistedDisplay, deletePersistedFilters } from "../store";

export function ClearFiltersButton() {
  const { dirtyFilters, resetFilters } = useView();
  return dirtyFilters ? (
    <Button size="sm" variant="ghost" onPress={() => resetFilters()}>
      Clear
    </Button>
  ) : null;
}

export function ClearDisplaySettingsButton() {
  const { dirtySettings, resetSettings } = useView();
  return dirtySettings ? (
    <Button size="sm" variant="ghost" onPress={() => resetSettings()}>
      <XIcon data-slot="icon" />
      <span className="hidden sm:block">Clear settings</span>
      <span className="block sm:hidden">Clear</span>
    </Button>
  ) : null;
}

export function ResetAllButton() {
  const { dirtyFilters, resetFilters } = useView();
  const { dirtySettings, resetSettings } = useView();
  return dirtyFilters || dirtySettings ? (
    <Button
      size="sm"
      variant="ghost"
      onPress={() => {
        resetFilters();
        resetSettings();
      }}
    >
      <XIcon data-slot="icon" />
      <span className="hidden sm:block">Reset view</span>
      <span className="block sm:hidden">Reset</span>
    </Button>
  ) : null;
}

const statuses = [
  "new",
  "received",
  "in_progress",
  "completed",
  "final",
  "cancelled",
] as const;

const statusOptions: { label: React.ReactNode; value: TAccessionStatus }[] =
  statuses.map((status) => ({
    label: (
      <div className="flex items-center gap-1">
        <StatusIcon status={status} />
        <span className="capitalize">{status.replace("_", " ")}</span>
      </div>
    ),
    value: status,
  }));

const holdOptions: {
  label: React.ReactNode;
  value: "active" | "resolved" | "canceled";
}[] = [
  {
    label: (
      <span className="flex items-center gap-2">
        <AlertOctagonIcon className="size-4 text-red-600" />
        Active
      </span>
    ),
    value: "active",
  },
  {
    label: (
      <span className="flex items-center gap-2">
        <CheckCircle2Icon className="size-4 text-green-600" />
        Resolved
      </span>
    ),
    value: "resolved",
  },
  {
    label: (
      <span className="flex items-center gap-2">
        <XCircleIcon className="size-4 text-muted-foreground" />
        Cancelled
      </span>
    ),
    value: "canceled",
  },
];

const typeOptions: { label: string; value: TTestType }[] = [
  {
    label: "Serology",
    value: "SEROLOGY",
  },
  {
    label: "Clinical",
    value: "CLINICAL",
  },
  {
    label: "HLA",
    value: "HLA",
  },
  {
    label: "Microbiology",
    value: "MICROBIOLOGY",
  },
];

const priorityOptions: { label: string; value: "STAT" | "ROUTINE" }[] = [
  {
    label: "STAT",
    value: "STAT",
  },
  {
    label: "ROUTINE",
    value: "ROUTINE",
  },
];

export function FilterBar() {
  const {
    additionalFilters,
    filters: accessionFilters,
    setFilters,
    isEditing,
    setIsEditing,
    isEditingLoading,
    dirtyFilters,
    settings,
  } = useView();
  const { data: primaryLabs } = trpc.orgs.primaryLabs.useQuery();
  const { data: orgs } = trpc.orgs.primaryOrgsList.useQuery();
  const { data: allOrgs } = trpc.orgs.list.useQuery();
  const { data: tests } = trpc.tests.allUsed.useQuery();
  const { data: panels } = trpc.panels.allUsed.useQuery();

  const filters = isEditing ? accessionFilters : additionalFilters;

  const onRemove = (keys: Set<Key>) => {
    if (keys.has("myAccessions")) {
      setFilters((s) => ({ ...s, myAccessions: false }));
    } else if (keys.has("hasSendOut")) {
      setFilters((s) => ({ ...s, hasSendOut: false }));
    } else {
      for (const key of keys) {
        const [filterKey, filterName] = key.toString().split("-");
        const currentFilters = filters[filterKey];
        setFilters((s) => ({
          ...s,
          [filterKey]:
            currentFilters?.filter(
              // biome-ignore lint/suspicious/noDoubleEquals: yes
              (f) => f != filterName,
            ) ?? [],
        }));
      }
    }
  };

  return dirtyFilters || isEditing ? (
    <div className="flex items-center justify-between px-4 py-3 border-b">
      <TagGroup
        onRemove={onRemove}
        aria-label="Accession filters"
        color="primary"
        variant="outline"
      >
        <TagList className="flex items-center flex-wrap gap-2">
          {filters.labs.map((lab) => (
            <Tag key={`lab-${lab}`} id={`lab-${lab}`}>
              Lab: {primaryLabs?.find((l) => l.id === lab)?.name || "-"}
            </Tag>
          ))}
          {filters.myAccessions && <Tag id="myAccessions">My accessions</Tag>}
          {filters.status.map((status) => (
            <Tag
              key={`status-${status}`}
              id={`status-${status}`}
              textValue={status}
            >
              Status: <StatusIcon status={status} className="size-3" />
              <span className="capitalize">
                {status?.replace("_", " ") || "-"}
              </span>
            </Tag>
          ))}
          {filters.holdStatus.map((status) => (
            <Tag
              key={`holdStatus-${status}`}
              id={`holdStatus-${status}`}
              textValue={status}
            >
              Hold: {status}
            </Tag>
          ))}
          {filters.client.map((client) => (
            <Tag
              key={`client-${client}`}
              id={`client-${client}`}
              textValue={client.toString()}
            >
              Client: {orgs?.find((o) => o.id === client)?.name || "-"}
            </Tag>
          ))}
          {filters.sharedClient.map((client) => (
            <Tag
              key={`sharedClient-${client}`}
              id={`sharedClient-${client}`}
              textValue={client.toString()}
            >
              Shared: {allOrgs?.find((o) => o.id === client)?.name || "-"}
            </Tag>
          ))}
          {filters.billToOrg.map((orgId) => (
            <Tag
              key={`billToOrg-${orgId}`}
              id={`billToOrg-${orgId}`}
              textValue={orgId.toString()}
            >
              Bill to: {allOrgs?.find((o) => o.id === orgId)?.name || "-"}
            </Tag>
          ))}
          {filters.splitBillingOrgs.map((orgId) => (
            <Tag
              key={`splitBillingOrgs-${orgId}`}
              id={`splitBillingOrgs-${orgId}`}
              textValue={orgId.toString()}
            >
              Split billing: {allOrgs?.find((o) => o.id === orgId)?.name || "-"}
            </Tag>
          ))}
          {filters.priority.map((priority) => (
            <Tag
              key={`priority-${priority}`}
              id={`priority-${priority}`}
              textValue={priority}
            >
              Priority: {priority}
            </Tag>
          ))}
          {filters.testType.map((type) => (
            <Tag
              key={`testType-${type}`}
              id={`testType-${type}`}
              textValue={type}
            >
              Type: {type}
            </Tag>
          ))}
          {filters.test.map((testId) => {
            const t = tests?.find((x) => x.id === testId);
            return (
              <Tag
                key={`test-${testId}`}
                id={`test-${testId}`}
                textValue={t ? `${t.name} (${t.code})` : testId.toString()}
              >
                Test: {t ? `${t.name} (${t.code})` : "-"}
              </Tag>
            );
          })}
          {filters.panel.map((panel) => (
            <Tag key={`panel-${panel}`} id={`panel-${panel}`}>
              Panel: {panels?.find((p) => p.id === panel)?.name || "-"}
            </Tag>
          ))}
          {filters.hasSendOut && <Tag id="hasSendOut">Send out</Tag>}
        </TagList>
      </TagGroup>
      <div className="flex items-center gap-2">
        {isEditing ? (
          <>
            <Button
              size="sm"
              variant="ghost"
              onPress={() => setIsEditing(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="outline"
              type="submit"
              form="view-edit"
              isPending={isEditingLoading}
            >
              Save
            </Button>
          </>
        ) : (
          <>
            <ClearFiltersButton />
            <SaveViewDialog
              filters={filters}
              display={settings.display}
              limit={settings.limit}
            >
              <Button size="sm" variant="outline">
                Save
              </Button>
            </SaveViewDialog>
          </>
        )}
      </div>
    </div>
  ) : null;
}

export function AccessionFilterPopover() {
  const { view, filters, setFilters, resetFilters, dirtyFilters } = useView();
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState("");
  const [search, setSearch] = useState("");
  const isMobile = useMediaQuery("(max-width: 600px)");

  const { user } = useAuth();
  const { data: primaryLabs } = trpc.orgs.primaryLabs.useQuery();
  const { data: orgs } = trpc.orgs.primaryOrgsList.useQuery();
  const { data: allOrgs } = trpc.orgs.list.useQuery();
  const { data: tests } = trpc.tests.allUsed.useQuery();
  const { data: panels } = trpc.panels.allUsed.useQuery();

  const allowedOrgs = (user?.profile.allowedOrgs as number[]) || [];
  const labRole = user?.hasRole([
    "blis_lab_admin",
    "blis_lab_manager",
    "blis_lab_tech",
    "blis_lab_assistant",
    "blis_lab_readonly",
  ]);
  const showClientFilter = labRole || allowedOrgs.length > 0;
  let orgOptions = orgs || [];
  if (!labRole && allowedOrgs.length > 0) {
    orgOptions = (orgs || []).filter(
      (o) => allowedOrgs.includes(o.id) || o.id === user?.profile.org?.id,
    );
  }

  useTinyKeys({
    f: (e) => {
      e.preventDefault();
      setOpen((open) => !open);
    },
  });

  const onOpenChange = (flag: boolean) => {
    if (!flag) {
      setTimeout(() => {
        setPage("");
      }, 200);
    }
    setSearch("");
    setOpen(flag);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: update on page change
  useEffect(() => {
    setSearch("");
  }, [page]);

  return (
    <DialogTrigger isOpen={open} onOpenChange={onOpenChange}>
      <Button size="sm" variant="outline" onPress={() => setOpen(true)}>
        <FilterIcon data-slot="icon" />
        Filter
      </Button>
      <Popover
        className="p-0 sm:min-w-[220px] sm:max-w-[300px]"
        placement="bottom end"
      >
        <Dialog
          className="p-0! outline-hidden"
          aria-label="Filter"
          hideCloseButton
        >
          <Command
            className="[&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2 [&_[cmdk-item]>svg]:size-4 [&_[cmdk-item]>svg]:mr-2"
            onKeyDown={(e) => {
              if (e.key === "Escape" && page) {
                e.preventDefault();
                e.stopPropagation();
                setPage("");
                setSearch("");
              }
              if (e.key.startsWith("Arrow")) {
                e.stopPropagation();
              }
            }}
          >
            <CommandInput
              placeholder="Filter..."
              value={search}
              onValueChange={setSearch}
              autoFocus={!isMobile}
              // biome-ignore lint/complexity/noUselessFragments: required
              icon={<></>}
            />
            <CommandEmpty>No filter found</CommandEmpty>
            <CommandList>
              <CommandGroup>
                {!page && (
                  <>
                    <CommandItem
                      onSelect={() => {
                        setFilters((s) => ({ ...s, myAccessions: true }));
                        onOpenChange(false);
                      }}
                    >
                      <UserIcon />
                      My accessions
                    </CommandItem>
                    <CommandItem onSelect={() => setPage("lab")}>
                      <BuildingIcon />
                      Lab
                    </CommandItem>
                    <CommandItem onSelect={() => setPage("status")}>
                      <StatusIcon status="new" />
                      Status
                    </CommandItem>
                    <CommandItem onSelect={() => setPage("hold")}>
                      <AlertOctagonIcon />
                      Hold
                    </CommandItem>
                    {showClientFilter && (
                      <CommandItem onSelect={() => setPage("client")}>
                        <BuildingIcon />
                        Client
                      </CommandItem>
                    )}
                    <CommandItem onSelect={() => setPage("sharedClient")}>
                      <BuildingIcon />
                      Shared client
                    </CommandItem>
                    <CommandItem onSelect={() => setPage("billToOrg")}>
                      <BuildingIcon />
                      Bill to
                    </CommandItem>
                    <CommandItem onSelect={() => setPage("splitBillingOrgs")}>
                      <BuildingIcon />
                      Split billing
                    </CommandItem>
                    <CommandItem onSelect={() => setPage("priority")}>
                      <FlagIcon />
                      Priority
                    </CommandItem>
                    <CommandItem onSelect={() => setPage("testType")}>
                      <FlaskConicalIcon />
                      Test type
                    </CommandItem>
                    <CommandItem onSelect={() => setPage("testSpec")}>
                      <FlaskConicalIcon />
                      Test spec
                    </CommandItem>
                    <CommandItem onSelect={() => setPage("panel")}>
                      <FlaskConicalIcon />
                      Panel
                    </CommandItem>
                    <CommandItem
                      onSelect={() => {
                        setFilters((s) => ({
                          ...s,
                          hasSendOut: !s.hasSendOut,
                        }));
                        onOpenChange(false);
                      }}
                    >
                      <SendIcon />
                      Has send out
                    </CommandItem>
                  </>
                )}
                {page === "lab" &&
                  primaryLabs?.map((opt) => (
                    <FilterCheckboxItem
                      key={opt.id}
                      value={opt.name}
                      checked={filters.labs.includes(opt.id)}
                      onCheckedChanged={(flag, close) => {
                        if (flag) {
                          setFilters((s) => ({
                            ...s,
                            labs: [...s.labs, opt.id],
                          }));
                        } else {
                          setFilters((s) => ({
                            ...s,
                            labs: s.labs.filter((s) => s !== opt.id),
                          }));
                        }
                        if (close) {
                          onOpenChange(false);
                        }
                      }}
                    >
                      {opt.name}
                    </FilterCheckboxItem>
                  ))}
                {page === "status" &&
                  statusOptions.map((opt) => (
                    <FilterCheckboxItem
                      key={opt.value}
                      value={opt.value}
                      disabled={view.filters.status.includes(opt.value)}
                      checked={filters.status.includes(opt.value)}
                      onCheckedChanged={(flag, close) => {
                        if (flag) {
                          setFilters((s) => ({
                            ...s,
                            status: [...s.status, opt.value],
                          }));
                        } else {
                          setFilters((s) => ({
                            ...s,
                            status: s.status.filter((s) => s !== opt.value),
                          }));
                        }
                        if (close) {
                          onOpenChange(false);
                        }
                      }}
                    >
                      {opt.label}
                    </FilterCheckboxItem>
                  ))}

                {page === "hold" &&
                  holdOptions.map((opt) => (
                    <FilterCheckboxItem
                      key={opt.value}
                      value={opt.value}
                      disabled={view.filters.holdStatus.includes(opt.value)}
                      checked={filters.holdStatus.includes(opt.value)}
                      onCheckedChanged={(flag, close) => {
                        if (flag) {
                          setFilters((s) => ({
                            ...s,
                            holdStatus: [...s.holdStatus, opt.value],
                          }));
                        } else {
                          setFilters((s) => ({
                            ...s,
                            holdStatus: s.holdStatus.filter(
                              (s) => s !== opt.value,
                            ),
                          }));
                        }
                        if (close) {
                          // onOpenChange(false);
                        }
                      }}
                    >
                      {opt.label}
                    </FilterCheckboxItem>
                  ))}
                {page === "client" &&
                  orgOptions.map((opt) => (
                    <FilterCheckboxItem
                      key={opt.id}
                      value={opt.name}
                      disabled={view.filters.client.includes(opt.id)}
                      checked={filters.client.includes(opt.id)}
                      onCheckedChanged={(flag, close) => {
                        if (flag) {
                          setFilters((s) => ({
                            ...s,
                            client: [...s.client, opt.id],
                          }));
                        } else {
                          setFilters((s) => ({
                            ...s,
                            client: s.client.filter((s) => s !== opt.id),
                          }));
                        }
                        if (close) {
                          // onOpenChange(false);
                        }
                      }}
                    >
                      {opt.name}
                    </FilterCheckboxItem>
                  ))}
                {page === "sharedClient" &&
                  allOrgs?.map((opt) => (
                    <FilterCheckboxItem
                      key={opt.id}
                      value={opt.name}
                      disabled={view.filters.sharedClient.includes(opt.id)}
                      checked={filters.sharedClient.includes(opt.id)}
                      onCheckedChanged={(flag, close) => {
                        if (flag) {
                          setFilters((s) => ({
                            ...s,
                            sharedClient: [...s.sharedClient, opt.id],
                          }));
                        } else {
                          setFilters((s) => ({
                            ...s,
                            sharedClient: s.sharedClient.filter(
                              (s) => s !== opt.id,
                            ),
                          }));
                        }
                        if (close) {
                          onOpenChange(false);
                        }
                      }}
                    >
                      {opt.name}
                    </FilterCheckboxItem>
                  ))}
                {page === "billToOrg" &&
                  allOrgs?.map((opt) => (
                    <FilterCheckboxItem
                      key={opt.id}
                      value={opt.name}
                      disabled={view.filters.billToOrg.includes(opt.id)}
                      checked={filters.billToOrg.includes(opt.id)}
                      onCheckedChanged={(flag, close) => {
                        if (flag) {
                          setFilters((s) => ({
                            ...s,
                            billToOrg: [...s.billToOrg, opt.id],
                          }));
                        } else {
                          setFilters((s) => ({
                            ...s,
                            billToOrg: s.billToOrg.filter((s) => s !== opt.id),
                          }));
                        }
                        if (close) {
                          onOpenChange(false);
                        }
                      }}
                    >
                      {opt.name}
                    </FilterCheckboxItem>
                  ))}
                {page === "splitBillingOrgs" &&
                  allOrgs?.map((opt) => (
                    <FilterCheckboxItem
                      key={opt.id}
                      value={opt.name}
                      disabled={view.filters.splitBillingOrgs.includes(opt.id)}
                      checked={filters.splitBillingOrgs.includes(opt.id)}
                      onCheckedChanged={(flag, close) => {
                        if (flag) {
                          setFilters((s) => ({
                            ...s,
                            splitBillingOrgs: [...s.splitBillingOrgs, opt.id],
                          }));
                        } else {
                          setFilters((s) => ({
                            ...s,
                            splitBillingOrgs: s.splitBillingOrgs.filter(
                              (s) => s !== opt.id,
                            ),
                          }));
                        }
                        if (close) {
                          onOpenChange(false);
                        }
                      }}
                    >
                      {opt.name}
                    </FilterCheckboxItem>
                  ))}
                {page === "priority" &&
                  priorityOptions.map((opt) => (
                    <FilterCheckboxItem
                      key={opt.value}
                      value={opt.label}
                      disabled={view.filters.priority.includes(opt.value)}
                      checked={filters.priority.includes(opt.value)}
                      onCheckedChanged={(flag, close) => {
                        if (flag) {
                          setFilters((s) => ({
                            ...s,
                            priority: [...s.priority, opt.value],
                          }));
                        } else {
                          setFilters((s) => ({
                            ...s,
                            priority: s.priority.filter((s) => s !== opt.value),
                          }));
                        }
                        if (close) {
                          onOpenChange(false);
                        }
                      }}
                    >
                      <span className="capitalize">{opt.label}</span>
                    </FilterCheckboxItem>
                  ))}
                {page === "testType" &&
                  typeOptions?.map((opt) => (
                    <FilterCheckboxItem
                      key={opt.value}
                      value={opt.label}
                      disabled={view.filters.testType.includes(opt.value)}
                      checked={filters.testType.includes(opt.value)}
                      onCheckedChanged={(flag, close) => {
                        if (flag) {
                          setFilters((s) => ({
                            ...s,
                            testType: [...s.testType, opt.value],
                          }));
                        } else {
                          setFilters((s) => ({
                            ...s,
                            testType: s.testType.filter((s) => s !== opt.value),
                          }));
                        }
                        onOpenChange(!close);
                      }}
                    >
                      <span className="capitalize">{opt.label}</span>
                    </FilterCheckboxItem>
                  ))}
                {page === "testSpec" &&
                  tests?.map((opt) => (
                    <FilterCheckboxItem
                      key={opt.id}
                      value={`${opt.name} ${opt.code}`}
                      disabled={view.filters.test.includes(opt.id)}
                      checked={filters.test.includes(opt.id)}
                      onCheckedChanged={(flag, close) => {
                        if (flag) {
                          setFilters((s) => ({
                            ...s,
                            test: [...s.test, opt.id],
                          }));
                        } else {
                          setFilters((s) => ({
                            ...s,
                            test: s.test.filter((s) => s !== opt.id),
                          }));
                        }
                        if (close) {
                          onOpenChange(false);
                        }
                      }}
                    >
                      <span className="flex flex-1 min-w-0 items-center justify-between gap-2">
                        <span className="min-w-0 truncate">{opt.name}</span>
                        <ItemDetail>{opt.code}</ItemDetail>
                      </span>
                    </FilterCheckboxItem>
                  ))}
                {page === "panel" &&
                  panels?.map((opt) => (
                    <FilterCheckboxItem
                      key={opt.id}
                      value={opt.name}
                      disabled={view.filters.panel.includes(opt.id)}
                      checked={filters.panel.includes(opt.id)}
                      onCheckedChanged={(flag, close) => {
                        if (flag) {
                          setFilters((s) => ({
                            ...s,
                            panel: [...s.panel, opt.id],
                          }));
                        } else {
                          setFilters((s) => ({
                            ...s,
                            panel: s.panel.filter((s) => s !== opt.id),
                          }));
                        }
                        if (close) {
                          onOpenChange(false);
                        }
                      }}
                    >
                      {opt.name}
                    </FilterCheckboxItem>
                  ))}
              </CommandGroup>
              {dirtyFilters && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        resetFilters();
                        onOpenChange(false);
                      }}
                    >
                      <XIcon className="size-4" />
                      <span>Clear filters</span>
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

function FilterCheckboxItem({
  checked,
  onCheckedChanged,
  children,
  value,
  disabled,
}: {
  checked: boolean;
  onCheckedChanged: (flag: boolean, close?: boolean) => void;
  children?: React.ReactNode;
  value: string;
  disabled?: boolean;
}) {
  return (
    <CommandItem
      disabled={disabled}
      onSelect={() => {
        if (checked) {
          onCheckedChanged(false, false);
        } else {
          onCheckedChanged(true, false);
        }
      }}
      className="flex items-center gap-2"
      value={value}
    >
      <Checkbox
        isSelected={checked}
        className="border-muted-foreground pointer-events-none flex-1 min-w-0"
        excludeFromTabOrder
      >
        {children}
      </Checkbox>
    </CommandItem>
  );
}

export function AccessionDisplayPopover() {
  const { view, setIsEditing, settings, setSettings, dirtySettings } =
    useView();
  const [open, setOpen] = useState(false);

  useTinyKeys({
    v: () => setOpen((open) => !open),
  });

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button
        size="sm"
        variant="outline"
        className="relative"
        onPress={() => setOpen(true)}
      >
        {dirtySettings && view.id > 0 && (
          <div className="absolute top-[1px] left-[16px] size-2.5 rounded-full bg-indigo-500 border-2 border-background z-10" />
        )}
        <SlidersHorizontalIcon className="size-3.5" />
        View
      </Button>
      <Popover className="p-0 sm:min-w-[280px]">
        <Dialog
          className="p-4 flex flex-col gap-4"
          aria-label="View options"
          hideCloseButton
        >
          {/* <Popover.Content align="end" className="flex flex-col gap-4"> */}
          <div className="hidden md:flex items-center justify-between">
            <Text>Display</Text>
            <Tabs
              variant="segment"
              selectedKey={settings.display}
              onSelectionChange={(key) => {
                setSettings((s) => ({
                  ...s,
                  display: key as "LIST" | "TABLE" | "BOARD",
                }));
              }}
            >
              <TabsList>
                <Tab id="LIST">List</Tab>
                <Tab id="TABLE">Table</Tab>
                <Tab id="BOARD">Board</Tab>
              </TabsList>
            </Tabs>
          </div>
          <div className="flex items-center justify-between">
            <Text>Dates</Text>
            <Tabs
              variant="segment"
              selectedKey={settings.dateRelative}
              onSelectionChange={(key) => {
                setSettings((s) => ({
                  ...s,
                  dateRelative: key as "actual",
                }));
              }}
            >
              <TabsList>
                <Tab id="actual">Actual</Tab>
                <Tab id="relative">Relative</Tab>
              </TabsList>
            </Tabs>
          </div>
          <div className="flex items-center justify-between">
            <Text>Limit</Text>
            <Select
              selectedKey={(settings.limit ?? 500).toString()}
              onSelectionChange={(key) => {
                setSettings((s) => ({
                  ...s,
                  limit: Number(key),
                }));
              }}
            >
              <SelectTrigger />
              <SelectList>
                {[50, 100, 500, 1000, -1].map((opt) => (
                  <SelectItem key={opt} id={opt.toString()}>
                    {opt === -1 ? "All" : opt}
                  </SelectItem>
                ))}
              </SelectList>
            </Select>
          </div>
          {view.id > 0 && (
            <RoleCondition roles={["blis_lab_manager"]}>
              <Separator />
              <div className="flex items-center justify-between">
                <DeleteViewDialog view={view}>
                  <Button variant="destructive" size="sm">
                    <TrashIcon />
                    Delete view
                  </Button>
                </DeleteViewDialog>
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => {
                    setIsEditing(true);
                    setOpen(false);
                  }}
                >
                  <PencilIcon />
                  Edit view
                </Button>
              </div>
            </RoleCondition>
          )}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

function SaveViewDialog({
  children,
  filters,
  display,
  limit,
}: {
  children: React.ReactNode;
  filters: AccessionListFilter;
  display: TView["display"];
  limit: TView["limit"];
}) {
  const { resetFilters, resetSettings } = useView();
  const mutate = trpc.accession.views.create.useMutation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const navigate = useNavigate();

  return (
    <DialogTrigger
      isOpen={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (isOpen) setName("");
      }}
    >
      {children}
      <Modal>
        <Dialog>
          <DialogHeader>
            <DialogTitle>New view</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Form
              id="save-view-form"
              onSubmit={(e) => {
                e.preventDefault();
                mutate.mutate(
                  { name, filters, display, limit },
                  {
                    onSuccess(data) {
                      resetFilters();
                      resetSettings();
                      setOpen(false);
                      navigate({
                        to: "/views/$id",
                        params: { id: data.id },
                      });
                    },
                  },
                );
              }}
            >
              <TextField id="name" value={name} onChange={setName} isRequired>
                <Label>View name</Label>
                <Input />
                <FieldError />
              </TextField>
            </Form>
          </DialogBody>
          <DialogFooter>
            <Button type="submit" form="save-view-form">
              Save
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}

function DeleteViewDialog({
  view,
  children,
}: {
  view: TView;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();

  const deleteView = trpc.accession.views.delete.useMutation(
    mutationResponse({
      errorTitle: "Failed to delete view",
      successMessage: "View deleted",
      onSuccess: () => {
        deletePersistedFilters(view);
        deletePersistedDisplay(view);
        navigate({
          to: "/accessions",
        });
      },
    }),
  );
  return (
    <ConfirmPrompt
      danger
      message="Are you sure you want to delete this view?"
      loading={deleteView.isPending}
      title="Delete view"
      onConfirm={() => {
        deleteView.mutate({ id: view.id });
      }}
    >
      {children}
    </ConfirmPrompt>
  );
}
